<script lang="ts" setup>
import { computed, ref } from "vue";
import BbelField from "@/views/builder/components/bbel/BbelField.vue";
import TemplateAuthFlowForm from "./TemplateAuthFlowForm.vue";
import {
  AUTH_FLOW_KINDS,
  CREDENTIAL_KINDS,
  type ConnectionFieldSpec,
  type CredentialRequirementSpec,
  type HttpMethodConfigSpec,
  type PlatformVariableSpec,
  type TemplateAuthMethodContent,
  type TransportConfigSpec,
} from "@/types";
import type { TemplateScopes } from "../composables/useConnectionTemplateScope";

const props = defineProps<{
  modelValue: TemplateAuthMethodContent;
  /** Per-phase BBEL scopes (request / response / outbound) for this method's fields. */
  scopes: TemplateScopes;
  /** Another method resolves to the same selection id — the publish gate will reject. */
  idConflict?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: TemplateAuthMethodContent];
}>();

function updateModel(patch: Partial<TemplateAuthMethodContent>) {
  emit("update:modelValue", { ...props.modelValue, ...patch });
}

// Protocol config lives under `http` (domain HttpMethodConfig) — base URL, auth flow, transport.
const http = computed(() => props.modelValue.http ?? null);

function updateHttp(patch: Partial<HttpMethodConfigSpec>) {
  updateModel({ http: { ...(props.modelValue.http ?? {}), ...patch } });
}

const kind = computed(() => http.value?.authFlow?.kind ?? "none");

// The method concerns are card-header tabs — a bounded pane visually subordinate to the method-level nav-tabs:
// declared values (connection fields + platform variables), the auth flow, and transport.
const activeSection = ref<"fields" | "flow" | "transport">("fields");

function updateKind(value: string) {
  updateHttp({
    authFlow: { ...(http.value?.authFlow ?? {}), kind: value },
  });
}

// ── "How this flow runs" — a popover stepper diagram off the Auth Flow tab, always derived ──────

const flowOpen = ref(false);

const TOKEN_BEARING = [
  "oauth2-authcode",
  "oauth2-clientcreds",
  "oauth2-password",
  "jwt-bearer",
  "vendor-initiated",
];

interface FlowChip {
  label: string;
  /** Wire identifier (mono) vs an external/system step (italic). */
  mono?: boolean;
  done?: boolean;
}

const narrative = computed(() => {
  const flow = http.value?.authFlow;
  const has = (n: string) => n in (flow?.handlers ?? {});
  const k = kind.value;
  const tokenBearing = TOKEN_BEARING.includes(k);
  const setup: FlowChip[] = [];
  const warnings: string[] = [];

  if (k === "oauth2-authcode") {
    setup.push({ label: "user connects" });
    if (has("authorize")) setup.push({ label: "authorize", mono: true });
    else warnings.push("authorize handler required");
    setup.push({ label: "vendor callback" });
  } else if (k === "vendor-initiated") {
    setup.push(
      { label: "vendor redirects the user" },
      { label: "inbound callback" }
    );
  } else {
    setup.push({ label: "user enters fields" });
  }
  if (tokenBearing) {
    if (has("token")) setup.push({ label: "token", mono: true });
    else warnings.push("token handler required");
    if (has("info")) setup.push({ label: "info", mono: true });
  }
  setup.push({ label: "connected", done: true });

  const outbound = flow?.outbound;
  const parts = [
    ...(outbound?.headers
      ? [`${Object.keys(outbound.headers).length} header(s)`]
      : []),
    ...(outbound?.query
      ? [`${Object.keys(outbound.query).length} query param(s)`]
      : []),
  ];
  const everyCall = parts.length
    ? `outbound ${parts.join(" + ")}`
    : "base URL only";

  const renewal = !tokenBearing
    ? null
    : has("refresh")
    ? "refresh"
    : "re-mints via token";
  const onDemand = ["invalidate", "test"].filter(has).join(", ") || null;

  return { setup, everyCall, renewal, onDemand, warnings };
});

// ── Connection field schema ────────────────────────────────────────────────────────────────────

const fields = computed(() => props.modelValue.connection ?? []);

function patchField(index: number, patch: Partial<ConnectionFieldSpec>) {
  updateModel({
    connection: fields.value.map((f, i) =>
      i === index ? { ...f, ...patch } : f
    ),
  });
}

function addField() {
  updateModel({
    connection: [...fields.value, { name: "", input: true, secret: false }],
  });
}

function removeField(index: number) {
  updateModel({ connection: fields.value.filter((_, i) => i !== index) });
}

// ── Platform variables ─────────────────────────────────────────────────────────────────────────

const platformVars = computed(() => props.modelValue.platform ?? []);

function patchVar(index: number, patch: Partial<PlatformVariableSpec>) {
  updateModel({
    platform: platformVars.value.map((v, i) =>
      i === index ? { ...v, ...patch } : v
    ),
  });
}

function addVar() {
  updateModel({
    platform: [...platformVars.value, { name: "", secret: true }],
  });
}

function removeVar(index: number) {
  updateModel({ platform: platformVars.value.filter((_, i) => i !== index) });
}

// ── Credentials block (§4.3) ───────────────────────────────────────────────────────────────────
// Declared roles make the `credentials` BBEL scope role-strict and drive the setup form's pickers.
// Removing the last row stores null (no block = scope stays open), never an empty array.

const credentialReqs = computed(() => props.modelValue.credentials ?? []);

function patchCredential(index: number, patch: Partial<CredentialRequirementSpec>) {
  updateModel({
    credentials: credentialReqs.value.map((c, i) =>
      i === index ? { ...c, ...patch } : c
    ),
  });
}

function addCredential() {
  updateModel({
    credentials: [...credentialReqs.value, { role: "", kind: "X509" }],
  });
}

function removeCredential(index: number) {
  const next = credentialReqs.value.filter((_, i) => i !== index);
  updateModel({ credentials: next.length ? next : null });
}

// mTLS points at a declared X509 role. A stored value the block no longer declares stays listed
// (flagged) so loading an older template never silently clobbers it; publish rejects it anyway.
const mtlsRoleOptions = computed(() =>
  credentialReqs.value.filter((c) => c.kind === "X509" && c.role).map((c) => c.role));
const mtlsRoleStale = computed(() => {
  const current = transport.value?.mtls?.credentialRole ?? "";
  return current !== "" && !mtlsRoleOptions.value.includes(current);
});

// ── Transport (mTLS / NTLM) ────────────────────────────────────────────────────────────────────

const transport = computed(() => http.value?.transportConfig ?? null);

function patchTransport(patch: Partial<TransportConfigSpec>) {
  const merged = { ...(transport.value ?? {}), ...patch };
  const empty = !merged.mtls && !merged.ntlm;
  updateHttp({ transportConfig: empty ? null : merged });
}

function toggleMtls(on: boolean) {
  patchTransport({ mtls: on ? { credentialRole: "" } : null });
}

function toggleNtlm(on: boolean) {
  patchTransport({ ntlm: on ? { username: "", password: "" } : null });
}
</script>

<template>
  <div>
    <div class="row g-3">
      <div class="col-12 col-md-3">
        <label class="form-label">Auth kind</label>
        <select
          class="form-select"
          :value="kind"
          @change="updateKind(($event.target as HTMLSelectElement).value)"
        >
          <option v-for="k in AUTH_FLOW_KINDS" :key="k" :value="k">
            {{ k }}
          </option>
        </select>
      </div>
      <div class="col-12 col-md-3">
        <label class="form-label">
          Method id
          <i
            class="bi bi-info-circle text-muted ms-1"
            data-bs-toggle="tooltip"
            title="Selection identity a connection records — defaults to the kind."
          ></i>
        </label>
        <input
          type="text"
          class="form-control"
          :class="{ 'is-invalid': idConflict }"
          :value="modelValue.id ?? ''"
          :placeholder="kind"
          @input="
            updateModel({
              id: ($event.target as HTMLInputElement).value || null,
            })
          "
        />
        <div v-if="idConflict" class="invalid-feedback">
          Another method resolves to this id — give each a distinct one.
        </div>
      </div>
      <div class="col-12 col-md-6">
        <label class="form-label">Label</label>
        <input
          type="text"
          class="form-control"
          :value="modelValue.label ?? ''"
          placeholder="Shown in the New Connection picker"
          @input="
            updateModel({
              label: ($event.target as HTMLInputElement).value || null,
            })
          "
        />
      </div>
      <div class="col-12">
        <label class="form-label">
          Base URL
          <i
            class="bi bi-info-circle text-muted ms-1"
            data-bs-toggle="tooltip"
            title="Literal or BBEL over connection fields; workflows compose paths onto it."
          ></i>
        </label>
        <BbelField
          :model-value="http?.baseUrl ?? ''"
          :scope="scopes.outbound"
          single-line
          placeholder="https://api.vendor.com/{{ connection.region }}"
          @update:model-value="updateHttp({ baseUrl: $event || null })"
        />
      </div>
    </div>

    <div class="card mt-4">
      <div class="card-header">
        <ul class="nav nav-tabs card-header-tabs">
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeSection === 'fields' }"
              @click="activeSection = 'fields'"
            >
              Fields
            </button>
          </li>
          <li class="nav-item position-relative">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeSection === 'flow' }"
              @click="activeSection = 'flow'"
            >
              Handlers
              <i
                class="bi bi-info-circle text-muted ms-1"
                role="button"
                title="How this flow runs"
                @click.stop="flowOpen = !flowOpen"
              ></i>
            </button>
            <!-- Bootstrap popover chrome on a Vue-rendered panel: the JS Popover can't host reactive
             content (the chips re-derive as handlers change), which justifies the manual wiring. -->
            <div
              v-if="flowOpen"
              class="popover bs-popover-bottom position-absolute top-100 start-0 z-3 mt-1"
              style="max-width: 40rem; width: max-content"
            >
              <div
                class="popover-header d-flex align-items-center justify-content-between"
              >
                <span>How this flow runs</span>
                <button
                  type="button"
                  class="btn-close ms-3"
                  @click="flowOpen = false"
                ></button>
              </div>
              <div class="popover-body">
                <div class="d-flex align-items-center gap-2 flex-wrap mb-2">
                  <template v-for="(chip, i) in narrative.setup" :key="i">
                    <i v-if="i > 0" class="bi bi-arrow-right text-muted"></i>
                    <code
                      v-if="chip.mono"
                      class="border rounded px-2 py-1 bg-body-tertiary"
                      >{{ chip.label }}</code
                    >
                    <strong v-else-if="chip.done" class="text-success">{{
                      chip.label
                    }}</strong>
                    <span v-else class="fst-italic text-muted">{{
                      chip.label
                    }}</span>
                  </template>
                </div>
                <div class="text-muted small">
                  Every call: {{ narrative.everyCall }}
                  <template v-if="narrative.renewal">
                    · On expiry: {{ narrative.renewal }}</template
                  >
                  <template v-if="narrative.onDemand">
                    · On demand: {{ narrative.onDemand }}</template
                  >
                </div>
                <div v-if="narrative.warnings.length" class="mt-2">
                  <span
                    v-for="w in narrative.warnings"
                    :key="w"
                    class="badge text-warning-emphasis bg-warning-subtle border border-warning-subtle me-1"
                    >{{ w }}</span
                  >
                </div>
              </div>
            </div>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeSection === 'transport' }"
              @click="activeSection = 'transport'"
            >
              Transport
            </button>
          </li>
        </ul>
      </div>
      <div class="card-body">
        <div v-show="activeSection === 'fields'">
          <label class="form-label">
            Connection Fields
            <i
              class="bi bi-info-circle text-muted ms-1"
              data-bs-toggle="tooltip"
              title="The method's field schema. User-input fields make up the setup form; secret fields are encrypted and never readable in expressions."
            ></i>
          </label>
          <div
            v-for="(field, i) in fields"
            :key="i"
            class="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="text"
              class="form-control flex-grow-1 font-monospace"
              placeholder="Field name"
              :value="field.name"
              @input="
                patchField(i, {
                  name: ($event.target as HTMLInputElement).value,
                })
              "
            />
            <div class="form-check form-check-inline mb-0 flex-shrink-0">
              <input
                :id="`field-input-${i}`"
                class="form-check-input"
                type="checkbox"
                :checked="!!field.input"
                @change="
                  patchField(i, {
                    input: ($event.target as HTMLInputElement).checked,
                  })
                "
              />
              <label class="form-check-label" :for="`field-input-${i}`"
                >User input</label
              >
            </div>
            <div class="form-check form-check-inline mb-0 flex-shrink-0">
              <input
                :id="`field-secret-${i}`"
                class="form-check-input"
                type="checkbox"
                :checked="!!field.secret"
                @change="
                  patchField(i, {
                    secret: ($event.target as HTMLInputElement).checked,
                  })
                "
              />
              <label class="form-check-label" :for="`field-secret-${i}`"
                >Secret</label
              >
            </div>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="removeField(i)"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addField"
            >
              <i class="bi bi-plus"></i> Add Field
            </button>
          </div>

          <label class="form-label mt-4">
            Platform Variables
            <i
              class="bi bi-info-circle text-muted ms-1"
              data-bs-toggle="tooltip"
              :title="'Platform-provisioned values this method requires (e.g. an OAuth client id/secret), referenced as {{ platform.name }}. Values live in the platform secret store — never in the template.'"
            ></i>
          </label>
          <div
            v-for="(pv, i) in platformVars"
            :key="i"
            class="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="text"
              class="form-control w-25 flex-shrink-0 font-monospace"
              placeholder="Variable name"
              :value="pv.name"
              @input="
                patchVar(i, { name: ($event.target as HTMLInputElement).value })
              "
            />
            <input
              type="text"
              class="form-control flex-grow-1"
              placeholder="Label"
              :value="pv.label ?? ''"
              @input="
                patchVar(i, {
                  label: ($event.target as HTMLInputElement).value || null,
                })
              "
            />
            <div class="form-check form-check-inline mb-0 flex-shrink-0">
              <input
                :id="`pv-secret-${i}`"
                class="form-check-input"
                type="checkbox"
                :checked="!!pv.secret"
                @change="
                  patchVar(i, {
                    secret: ($event.target as HTMLInputElement).checked,
                  })
                "
              />
              <label class="form-check-label" :for="`pv-secret-${i}`"
                >Secret</label
              >
            </div>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="removeVar(i)"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addVar"
            >
              <i class="bi bi-plus"></i> Add Variable
            </button>
          </div>

          <label class="form-label mt-4">
            Credentials
            <i
              class="bi bi-info-circle text-muted ms-1"
              data-bs-toggle="tooltip"
              :title="'Credentials the connection must link, by role — referenced as {{ credentials[\'role\'].field }} and prompted for (kind-filtered) on the setup form. Declaring any role makes references role-strict at publish.'"
            ></i>
          </label>
          <div
            v-for="(cr, i) in credentialReqs"
            :key="i"
            class="d-flex align-items-center gap-2 mb-2"
          >
            <input
              type="text"
              class="form-control w-25 flex-shrink-0 font-monospace"
              placeholder="Role (e.g. jwt-signing)"
              :value="cr.role"
              @input="
                patchCredential(i, {
                  role: ($event.target as HTMLInputElement).value,
                })
              "
            />
            <select
              class="form-select w-auto flex-shrink-0"
              :value="cr.kind"
              @change="
                patchCredential(i, {
                  kind: ($event.target as HTMLSelectElement).value,
                })
              "
            >
              <option v-for="k in CREDENTIAL_KINDS" :key="k" :value="k">
                {{ k }}
              </option>
            </select>
            <input
              type="text"
              class="form-control flex-grow-1"
              placeholder="Label shown on the setup form"
              :value="cr.label ?? ''"
              @input="
                patchCredential(i, {
                  label: ($event.target as HTMLInputElement).value || null,
                })
              "
            />
            <div class="form-check form-check-inline mb-0 flex-shrink-0">
              <input
                :id="`cred-optional-${i}`"
                class="form-check-input"
                type="checkbox"
                :checked="!!cr.optional"
                @change="
                  patchCredential(i, {
                    optional: ($event.target as HTMLInputElement).checked,
                  })
                "
              />
              <label class="form-check-label" :for="`cred-optional-${i}`"
                >Optional</label
              >
            </div>
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="removeCredential(i)"
            >
              <i class="bi bi-trash"></i>
            </button>
          </div>
          <div>
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              @click="addCredential"
            >
              <i class="bi bi-plus"></i> Add Credential
            </button>
          </div>
        </div>

        <div v-if="http?.authFlow" v-show="activeSection === 'flow'">
          <TemplateAuthFlowForm
            :model-value="http.authFlow"
            :scopes="scopes"
            :connection-fields="fields.map((f) => f.name)"
            @update:model-value="updateHttp({ authFlow: $event })"
          />
        </div>

        <div v-show="activeSection === 'transport'">
          <div class="form-check form-switch">
            <input
              id="transport-mtls"
              class="form-check-input"
              type="checkbox"
              :checked="!!transport?.mtls"
              @change="toggleMtls(($event.target as HTMLInputElement).checked)"
            />
            <label class="form-check-label" for="transport-mtls">
              Client certificate (mTLS)
              <i
                class="bi bi-info-circle text-muted ms-1"
                data-bs-toggle="tooltip"
                title="Presents a client certificate on every request. Pick the X509 credential role declared on the Fields tab; each connection links the actual certificate at setup."
              ></i>
            </label>
          </div>
          <div v-if="transport?.mtls" class="mt-2 ms-4 col-md-4">
            <select
              class="form-select font-monospace"
              :class="{ 'is-invalid': mtlsRoleStale }"
              :value="transport.mtls.credentialRole"
              @change="
                patchTransport({
                  mtls: {
                    credentialRole: ($event.target as HTMLSelectElement).value,
                  },
                })
              "
            >
              <option value="">Certificate role…</option>
              <option v-for="r in mtlsRoleOptions" :key="r" :value="r">
                {{ r }}
              </option>
              <option
                v-if="mtlsRoleStale"
                :value="transport.mtls.credentialRole"
              >
                {{ transport.mtls.credentialRole }} (not declared)
              </option>
            </select>
            <div v-if="mtlsRoleStale" class="invalid-feedback">
              Not declared on the Fields tab — publish will reject it.
            </div>
            <div
              v-else-if="mtlsRoleOptions.length === 0"
              class="form-text"
            >
              Declare an X509 credential on the Fields tab first.
            </div>
          </div>

          <div class="form-check form-switch mt-3">
            <input
              id="transport-ntlm"
              class="form-check-input"
              type="checkbox"
              :checked="!!transport?.ntlm"
              @change="toggleNtlm(($event.target as HTMLInputElement).checked)"
            />
            <label class="form-check-label" for="transport-ntlm">
              NTLM credentials
              <i
                class="bi bi-info-circle text-muted ms-1"
                data-bs-toggle="tooltip"
                title="Requests authenticate with Windows NTLM. Values are BBEL over the method's connection fields."
              ></i>
            </label>
          </div>
          <div v-if="transport?.ntlm" class="mt-2 ms-4">
            <div class="row g-3">
              <div class="col-12 col-md-4">
                <label class="form-label">Username</label>
                <BbelField
                  :model-value="transport.ntlm.username"
                  :scope="scopes.outbound"
                  single-line
                  placeholder="{{ connection.username }}"
                  @update:model-value="
                    patchTransport({
                      ntlm: { ...transport!.ntlm!, username: $event },
                    })
                  "
                />
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label">Password</label>
                <BbelField
                  :model-value="transport.ntlm.password"
                  :scope="scopes.outbound"
                  single-line
                  placeholder="{{ credentials.password }}"
                  @update:model-value="
                    patchTransport({
                      ntlm: { ...transport!.ntlm!, password: $event },
                    })
                  "
                />
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label">
                  Domain
                  <span class="text-muted fw-normal">(optional)</span>
                </label>
                <BbelField
                  :model-value="transport.ntlm.domain ?? ''"
                  :scope="scopes.outbound"
                  single-line
                  placeholder="CORP"
                  @update:model-value="
                    patchTransport({
                      ntlm: { ...transport!.ntlm!, domain: $event || null },
                    })
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
